import React, { useState } from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	Radio,
	RadioGroup,
	Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { IPassenger } from '../../types';

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
	open: boolean;
	invoiceablePassengers: IPassenger[];
	loading: boolean;
	onConfirm: (passengerId: string) => void;
	onClose: () => void;
}

// ─── Modal ────────────────────────────────────────────────────────────────────

export const GenerateReceiptModal = ({
	open,
	invoiceablePassengers,
	loading,
	onConfirm,
	onClose,
}: Props): JSX.Element => {
	const [selectedId, setSelectedId] = useState<string>('');

	const hasPassengers = invoiceablePassengers.length > 0;
	const isSingle = invoiceablePassengers.length === 1;

	const handleClose = () => {
		if (!loading) {
			setSelectedId('');
			onClose();
		}
	};

	const handleConfirm = () => {
		if (!selectedId) return;
		const pax = invoiceablePassengers.find((p) => p.keyId === selectedId);
		if (pax) onConfirm(pax.id || pax.keyId);
	};

	const passengerLabel = (p: IPassenger): string => {
		const name = `${p.name} ${p.lastName}`.trim();
		const doc = p.documentNumber ? ` — Doc: ${p.documentNumber}` : '';
		const main = p.mainPassenger ? ' (principal)' : '';
		return `${name}${doc}${main}`;
	};

	// Si hay exactamente 1, el padre llama directo sin abrir el modal
	if (isSingle) return <></>;

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
			{!hasPassengers ? (
				/* ── Sin pasajeros facturables ── */
				<>
					<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<WarningAmberIcon color="warning" />
						No se puede generar el recibo
					</DialogTitle>
					<DialogContent>
						<Typography variant="body2" color="text.secondary">
							Esta orden no tiene pasajeros con datos de facturación completos. Para poder generar el recibo, al menos
							un pasajero debe estar marcado como facturable.
						</Typography>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleClose} variant="contained" disableElevation>
							Entendido
						</Button>
					</DialogActions>
				</>
			) : (
				/* ── Múltiples pasajeros: seleccionar uno ── */
				<>
					<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<ReceiptIcon color="primary" />
						Seleccioná el pasajero facturable
					</DialogTitle>
					<DialogContent>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							Esta orden tiene más de un pasajero facturable. Seleccioná a quién se emitirá el recibo.
						</Typography>
						<FormControl component="fieldset" fullWidth>
							<RadioGroup value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
								{invoiceablePassengers.map((p) => (
									<Box
										key={p.keyId}
										sx={{
											border: '1px solid',
											borderColor: selectedId === p.keyId ? 'primary.main' : 'divider',
											borderRadius: 1,
											mb: 1,
											px: 1,
											bgcolor: selectedId === p.keyId ? 'rgba(25,118,210,0.06)' : 'transparent',
											transition: 'all 0.15s',
										}}
									>
										<FormControlLabel
											value={p.keyId}
											control={<Radio size="small" />}
											label={passengerLabel(p)}
											sx={{ width: '100%', m: 0, py: 0.5 }}
										/>
									</Box>
								))}
							</RadioGroup>
						</FormControl>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleClose} disabled={loading}>
							Cancelar
						</Button>
						<Button
							variant="contained"
							disableElevation
							onClick={handleConfirm}
							disabled={!selectedId || loading}
							startIcon={<ReceiptIcon />}
						>
							{loading ? 'Generando...' : 'Generar recibo'}
						</Button>
					</DialogActions>
				</>
			)}
		</Dialog>
	);
};
